import { useCallback, useMemo, type ReactElement } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { usePermission } from '@/Hooks/usePermission';

/**
 * ## A tab is a place, so it gets a URL
 *
 * Every tabbed screen in this app held its open tab in `useState`, and all four
 * consequences of that were reported by the same person four times: a tab
 * cannot be linked to, a reload throws you back to the first one, the back
 * button leaves the page instead of returning to the tab you came from, and a
 * bookmark points at a screen rather than at the thing you bookmarked.
 *
 * `Settings` already did this properly and is where the shape comes from. This
 * is that logic taken out of it, so the eight screens that follow cannot each
 * invent a slightly different answer to the three questions it has to settle:
 *
 *  - **The bare path.** `/catalog` is a real thing to type and a real thing to
 *    link. It redirects to the first tab this actor may see — which is not the
 *    same tab for everyone, because the list is permission-filtered — with
 *    `replace`, so the redirect does not become a history entry the back button
 *    bounces off on the way out of the screen.
 *  - **A tab this actor may not see.** 403, the same refusal the rest of the app
 *    gives at the route. Silently landing them on a different tab from the one
 *    the link named is the version that reads as broken: nothing says what
 *    happened, and the page they are looking at is not the page they asked for.
 *  - **A segment that is not a tab at all.** A typo or a renamed tab in an old
 *    link. It is not a refusal, so it lands on the first tab rather than a 403.
 *
 * Choosing a tab **pushes**, which is the whole of the back-button fix: back
 * from tab three goes to tab two, and only leaves the screen once it runs out
 * of tabs to go back through.
 */
export interface RoutedTab<T extends string> {
  id: T;
  /** The label's i18n key. */
  labelKey: string;
  /** Any one of these shows the tab. Absent or empty means everybody. */
  perms?: string[];
}

export type RoutedTabs<T extends string> = {
  /**
   * Open a tab. Pushes, so back returns to the tab that was open before.
   *
   * Outside the union because a screen can want it before it knows which tab
   * is open — the inventory board's "no active store" banner offers a jump to
   * the warehouses tab from a page that has already returned early.
   */
  select: (id: T) => void;
} & (
  | /** The URL names a tab this actor may see. */ {
      open: true;
      /** The tabs this actor may see, in the order they were declared. */
      tabs: RoutedTab<T>[];
      current: T;
    }
  /** It does not, and this is where they go instead. Render it and nothing else. */
  | { open: false; redirect: ReactElement }
);

export function useRoutedTabs<T extends string>(
  /** The screen's path, with no trailing slash — `/catalog`. */
  basePath: string,
  all: readonly RoutedTab<T>[],
): RoutedTabs<T> {
  const { canAny } = usePermission();
  const navigate = useNavigate();
  const { tab: segment } = useParams<{ tab: string }>();

  const tabs = useMemo(
    () => all.filter((tab) => !tab.perms?.length || canAny(...tab.perms)),
    [all, canAny],
  );
  const select = useCallback(
    (id: T) => navigate(`${basePath}/${id}`),
    [basePath, navigate],
  );

  if (tabs.some((tab) => tab.id === segment))
    return { select, open: true, tabs, current: segment as T };

  // Every tab of this screen is withheld, so the screen is. Reachable only if a
  // route gate is looser than the tabs behind it, which is a bug worth seeing.
  if (!tabs.length) return { select, open: false, redirect: <Navigate to="/403" replace /> };

  // A real tab, withheld from this actor.
  if (all.some((tab) => tab.id === segment))
    return { select, open: false, redirect: <Navigate to="/403" replace /> };

  // The bare path, or a segment that names no tab at all.
  return { select, open: false, redirect: <Navigate to={`${basePath}/${tabs[0].id}`} replace /> };
}
